import { useSyncExternalStore } from "react";
import { api, API_ROOT, CLIENT_HEADER } from "@/api/client";
import { deletePushBinding, readPushBinding, setBadge, writePushBinding } from "./pushStore";
import type { PushBinding } from "./pushStore";

type PushError = "denied" | "failed" | "unsupported" | null;
interface PushState { enabled: boolean; busy: boolean; serverEnabled: boolean; error: PushError }
let state: PushState = { enabled: false, busy: false, serverEnabled: true, error: null };
const listeners = new Set<() => void>();
let generation = 0;
let queue = Promise.resolve();
function set(values: Partial<PushState>) {
    state = { ...state, ...values };
    listeners.forEach(listener => listener());
}
export const pushSupported = () => import.meta.env.PROD && window.isSecureContext
    && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export function usePushState() {
    return useSyncExternalStore(listener => {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    }, () => state);
}
function publicKey(value: string) {
    const text = atob(value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4));
    return Uint8Array.from(text, char => char.charCodeAt(0));
}
async function registration() {
    if (!pushSupported()) throw new Error("Unsupported");
    // This registration is also used by the silent updater. Never register another worker.
    const current = await navigator.serviceWorker.getRegistration();
    if (!current) await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
    return navigator.serviceWorker.ready;
}
async function registerDevice(userId: number, run: number, explicit: boolean) {
    const previous = await readPushBinding();
    if (!explicit && (!previous?.enabled || previous.user_id !== userId)) {
        if (previous?.enabled && previous.user_id !== userId) await disablePush();
        return;
    }
    if (!pushSupported()) { set({ error: explicit ? "unsupported" : null }); return; }
    if (Notification.permission !== "granted") {
        set({ enabled: false, error: explicit ? "denied" : null });
        return;
    }
    const config = (await api.raw<{ enabled: boolean; public_key: string }>("/push/status")).data;
    if (run !== generation) return;
    set({ serverEnabled: config.enabled });
    if (!config.enabled) { set({ enabled: false }); return; }
    const worker = await registration();
    const key = publicKey(config.public_key);
    let subscription = await worker.pushManager.getSubscription();
    // Site storage may have been cleared while the browser kept its subscription.
    // Mint a new endpoint instead of trying to claim a binding whose token was lost.
    if (subscription && !previous) {
        await subscription.unsubscribe();
        subscription = null;
    }
    const existingKey = subscription?.options?.applicationServerKey;
    if (existingKey && (existingKey.byteLength !== key.byteLength
        || new Uint8Array(existingKey).some((value, index) => value !== key[index]))) {
        await subscription?.unsubscribe();
        subscription = null;
    }
    if (!subscription) subscription = await worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    if (previous && previous.endpoint !== subscription.endpoint) {
        await api.post("/push/unsubscribe", { endpoint: previous.endpoint, token: previous.token }).catch(() => {});
    }
    if (run !== generation) return;
    const payload = (await api.post<Omit<PushBinding, "enabled">>("/push/subscribe", {
        subscription: subscription.toJSON(), token: previous?.endpoint === subscription.endpoint ? previous.token : "",
    })).data;
    if (run !== generation) {
        void api.post("/push/unsubscribe", { endpoint: payload.endpoint, token: payload.token }).catch(() => {});
        return;
    }
    // Rebinding invalidates notifications left by the previous account on this browser.
    if (previous?.binding_id !== payload.binding_id) {
        for (const notification of await worker.getNotifications()) notification.close();
        await setBadge(0);
    }
    await writePushBinding({ ...payload, enabled: true }, () => run === generation);
    if (run === generation) set({ enabled: true, error: null });
}
export function syncPush(userId: number) {
    const run = ++generation;
    queue = queue.catch(() => {}).then(async () => {
        if (run !== generation) return;
        try { await registerDevice(userId, run, false); }
        catch { /* A temporary outage does not remove the existing device binding. */ }
    });
    return queue;
}
export async function enablePush(userId: number) {
    if (state.busy) return;
    if (!pushSupported()) { set({ error: "unsupported" }); return; }
    const run = ++generation;
    set({ busy: true, error: null });
    try {
        // Keep the permission prompt inside the original user gesture, including on Safari.
        const permission = await Notification.requestPermission();
        if (permission !== "granted") { set({ error: "denied" }); return; }
        await queue.catch(() => {});
        if (run === generation) await registerDevice(userId, run, true);
    } catch { set({ error: "failed" }); }
    finally { set({ busy: false }); }
}
export async function disablePush() {
    ++generation;
    set({ enabled: false, busy: true, error: null });
    try {
        const binding = await readPushBinding();
        // Leave a disabled capability until removal succeeds, so a failed/offline
        // unsubscribe can be retried without losing proof that this is our device.
        if (binding) {
            try { await writePushBinding({ ...binding, enabled: false }); }
            catch { await deletePushBinding(); } // Deletion can still succeed when quota prevents a write.
        }
        await setBadge(0);
        const worker = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
        for (const notification of await worker?.getNotifications() ?? []) notification.close();
        const subscription = await worker?.pushManager.getSubscription();
        const results = await Promise.allSettled([
            binding ? fetch(`${API_ROOT}/push/unsubscribe`, {
                method: "POST", credentials: "same-origin", cache: "no-store",
                headers: { "Content-Type": "application/json", [CLIENT_HEADER]: "promaster-react" },
                body: JSON.stringify({ endpoint: binding.endpoint, token: binding.token }),
                signal: AbortSignal.timeout(3000),
            }).then(response => { if (!response.ok) throw new Error("Unsubscribe failed"); }) : Promise.resolve(),
            subscription?.unsubscribe() ?? Promise.resolve(),
        ]);
        if (results.every(result => result.status === "fulfilled")) await deletePushBinding();
    } catch { /* Retain a disabled capability for a later server removal. */ }
    finally {
        await setBadge(0);
        try {
            const worker = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
            for (const notification of await worker?.getNotifications() ?? []) notification.close();
        } catch { /* A browser can be shutting down its worker. */ }
        set({ busy: false });
    }
}
